import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import { Topbar } from "./components/shared/Topbar.tsx";
import { ErrorBoundary } from "./components/shared/ErrorBoundary.tsx";
import { ToastProvider } from "./components/shared/Toast.tsx";
import { ThemeProvider } from "./lib/theme-context.tsx";
import { AuthProvider, useAuth } from "./lib/auth-context.tsx";
import { LoginPage, BetaActivation } from "./pages/LoginPage.tsx";
import { AuthCallback } from "./pages/AuthCallback.tsx";
import { ProjectList } from "./pages/ProjectList.tsx";
import { OnboardingWizard } from "./pages/OnboardingWizard.tsx";
import { ProjectDashboard } from "./pages/ProjectDashboard.tsx";
import { LicenseDetail } from "./pages/LicenseDetail.tsx";
import { ProjectShell } from "./pages/ProjectShell.tsx";
import { Checkup } from "./pages/Checkup.tsx";
import InterviewForm from "./pages/InterviewForm.tsx";
import { AdminDashboard } from "./pages/AdminDashboard.tsx";
import { PrivacyPolicy } from "./pages/PrivacyPolicy.tsx";
import { TermsOfService } from "./pages/TermsOfService.tsx";

/** Redirect logged-in users from /login, preserving checkup params */
function LoginRedirect() {
  const [params] = useSearchParams();
  if (params.get("from") === "checkup") {
    const token = params.get("checkup_token") || "";
    return <Navigate to={`/projects/new?from=checkup${token ? `&checkup_token=${token}` : ""}`} replace />;
  }
  return <Navigate to="/projects" replace />;
}

function AuthenticatedApp() {
  const { user, loading, betaRequired } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-secondary flex items-center justify-center">
        <div className="text-text-tertiary text-[16px]">Loading...</div>
      </div>
    );
  }

  if (!user && betaRequired) {
    return <BetaActivation />;
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  return (
    <div className="min-h-screen bg-surface-secondary">
      <Routes>
        <Route path="/" element={<Navigate to="/projects" replace />} />
        <Route path="/login" element={<LoginRedirect />} />
        <Route path="/auth/callback" element={<AuthCallback />} />

        <Route path="/projects" element={
          <>
            <Topbar />
            <ProjectList />
          </>
        } />
        <Route path="/projects/new" element={
          <>
            <Topbar />
            <OnboardingWizard />
          </>
        } />

        <Route path="/projects/:id" element={<ProjectShell />}>
          <Route index element={<ProjectDashboard />} />
          <Route path="licenses/:licenseId" element={<LicenseDetail />} />
        </Route>
      </Routes>
    </div>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
      <ToastProvider>
      <BrowserRouter>
        <Routes>
          {/* Public routes — no auth required */}
          <Route path="/checkup" element={
            <div className="min-h-screen bg-surface-secondary">
              <Checkup />
            </div>
          } />
          <Route path="/interview" element={<InterviewForm />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/admin" element={
            <div className="min-h-screen bg-surface-secondary">
              <AdminDashboard />
            </div>
          } />
          {/* All other routes go through auth */}
          <Route path="*" element={
            <AuthProvider>
              <AuthenticatedApp />
            </AuthProvider>
          } />
        </Routes>
      </BrowserRouter>
      </ToastProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
